import { Component, type ErrorInfo, type ReactNode } from 'react';
import { strings } from '../lib/strings.tsx';
import './ErrorBoundary.css';

/**
 * **화면이 터지면 흰 화면이 된다** — 그 자리에 말을 남긴다 (결정2403).
 *
 * 〔실측〕 이 콘솔에 오류 경계가 **하나도 없었다.** React 는 렌더 중 예외가 나면 트리를
 * 통째로 **언마운트한다** — 사용자에게는 **빈 페이지**만 남는다. 무엇이 났는지도,
 * 무엇을 하면 되는지도, 심지어 **터졌다는 사실조차** 안 보인다.
 *
 * 이 저장소가 되풀이해 세운 잣대의 극단이다:
 *
 * ```
 * 조용히 실패하지 마라        빈 화면은 실패를 **말하지도 않는다**
 * 가려진 것은 없는 것이다      사용자는 "느리다" 로 읽고 기다린다
 * 다음에 무엇을 할지 말하라    빈 화면은 아무것도 못 하게 한다
 * ```
 *
 * ## 무엇을 하고 무엇을 안 하나
 *
 * ```
 * 한다     터졌다고 말한다 · **오류 원문**을 보인다 · 다시 시도할 길을 준다
 * 한다     콘솔에도 남긴다 — 개발자 도구를 여는 사람이 스택을 봐야 한다
 * 안 한다  **삼키지 않는다.** 오류를 감추고 빈 자리를 그리면 지금과 같아진다
 * 안 한다  자동 재시도 — 터진 채로 다시 그리면 무한히 터진다
 * ```
 *
 * ⚠ **원문을 그대로 보인다.** 여기서 사람 말로 바꾸면 서버·라이브러리가 준 단서가
 * 사라진다 — 이 화면을 볼 사람은 대개 운영자다.
 */
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | undefined;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: undefined };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* 개발자 도구를 여는 사람에게는 스택이 필요하다 — 화면 문구로는 부족하다. */
    // eslint-disable-next-line no-console
    console.error('[console] render failed', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === undefined) return this.props.children;
    return (
      <div className="error-boundary" role="alert">
        <h2>{strings.errorBoundary.title}</h2>
        <p>{strings.errorBoundary.hint}</p>
        {/* 원문. 접지 않는다 — 이걸 보려고 여기까지 온 것이다. */}
        <pre className="error-boundary__detail">{error.message}</pre>
        <button type="button" onClick={() => window.location.reload()}>
          {strings.errorBoundary.reload}
        </button>
      </div>
    );
  }
}
